import {
  ApolloClient,
  ApolloLink,
  defaultDataIdFromObject,
  HttpLink,
  InMemoryCache,
  Observable,
} from '@apollo/client'
import { GraphQLWsLink } from '@apollo/client/link/subscriptions'
import { getMainDefinition } from '@apollo/client/utilities'
import { getJwtExpiresAt, isJwtExpiringWithin } from '@sections/auth/jwt'
import { createAuthLink, ensureFreshAccessToken, getBaseHeaders } from '@sections/auth/link'
import { getTokens } from '@sections/auth/utils'
import type { Locale } from '@ui/Locale'
import { CloseCode, createClient } from 'graphql-ws'
import { camelCase } from 'lodash-es'

import fragmentTypes from '../../gql/fragmentTypes.generated'
import pkg from '../../package.json' with { type: 'json' }
import type { ApolloHandlers } from './handlers'
import { createAuthErrorLink } from './links/authErrorLink'
import { getHiddenDuration, whenPageVisible } from './pageActivity'
import QueryPolicies from './queryPolicies'
import { registerWsTerminate } from './wsAuth'
import { reportWsConnected } from './wsEvents'

// Dead-socket detection cadence (see the ws client `on` block below): ping the
// server on idle, and if no pong returns within the grace window, force-close
// so retry + reconcile-on-reconnect can heal what the dead socket missed.
const WS_KEEPALIVE_MS = 10_000
const WS_PONG_TIMEOUT_MS = 5_000

// Token lifecycle on the socket (graphql-ws "auth + token refresh" recipe).
// The server validates the token captured at connect time on every subscribe,
// so a socket older than the token's lifetime rejects new subscriptions even
// though the app has long refreshed over HTTP. We therefore (a) hand every
// connect a token good for at least `WS_TOKEN_MIN_TTL_MS`, refreshing first
// if needed, and (b) close the socket ourselves shortly before its token
// expires, so the automatic reconnect picks up a fresh one.
const WS_TOKEN_MIN_TTL_MS = 60_000
const WS_TOKEN_EXPIRY_CLOSE_MARGIN_MS = 30_000

// Reconnect pacing. graphql-ws' default backoff is 2^retries seconds with no
// cap, which with unlimited attempts means a long outage ends in waits of many
// minutes; cap it. And a tab hidden for `WS_STALE_HIDDEN_MS` is stale: we drop
// its socket and park any reconnect until the tab is on screen again
// (reconnect + reconcile-on-reconnect happen the moment it is).
const WS_RETRY_BASE_MS = 1_000
const WS_RETRY_MAX_MS = 30_000
const WS_RETRY_JITTER_MS = 3_000
const WS_STALE_HIDDEN_MS = 60 * 60_000

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms))

const mutationInvalidationLink = new ApolloLink(
  (operation, forward) =>
    new Observable((observer) => {
      const subscription = forward(operation).subscribe({
        complete: observer.complete.bind(observer),
        error: observer.error.bind(observer),
        next: (result) => {
          if (
            operation.operationName &&
            operation.query.definitions.some(
              (d) => d.kind === 'OperationDefinition' && d.operation === 'mutation',
            )
          ) {
            if (result.data) {
              const match = operation.operationName.match(/^(.*)(?:Create|Update|Delete)$/)
              if (match) {
                const cache = operation.client.cache
                if (cache instanceof InMemoryCache) {
                  cache.evict({ fieldName: camelCase(match[1] + 's') })
                  cache.gc()
                }
              }
            }
          }

          observer.next(result)
        },
      })
      return () => subscription.unsubscribe()
    }),
)

export const createApolloClient = (locale: Locale, getHandlers: () => ApolloHandlers) => {
  const httpLink = ApolloLink.from([
    createAuthErrorLink(getHandlers),
    createAuthLink(locale),
    mutationInvalidationLink,
    new HttpLink({
      credentials: 'include',
      uri: import.meta.env.VITE_API_URI,
    }),
  ])

  // Dead-socket detection (graphql-ws canonical recipe). A subscription
  // socket is receive-only, so a connection silently dropped by a NAT/proxy
  // idle timeout, laptop sleep, or Cloud Run's request timeout leaves the
  // browser's socket stuck OPEN forever: no close frame ever arrives, so
  // `retryAttempts` never fires and live delivery dies while still "looking
  // connected". We ping the server every `WS_KEEPALIVE_MS`; if no pong comes
  // back within `WS_PONG_TIMEOUT_MS` we close the socket ourselves (4408),
  // which DOES trigger retry + reconcile-on-reconnect.
  let activeSocket: WebSocket | undefined
  let pongTimeout: ReturnType<typeof setTimeout> | undefined
  let tokenExpiryTimeout: ReturnType<typeof setTimeout> | undefined
  let staleHiddenTimeout: ReturnType<typeof setTimeout> | undefined
  const clearSocketTimers = () => {
    clearTimeout(pongTimeout)
    clearTimeout(tokenExpiryTimeout)
    clearTimeout(staleHiddenTimeout)
    pongTimeout = tokenExpiryTimeout = staleHiddenTimeout = undefined
  }

  const wsClient = createClient({
    connectionParams: async () => {
      // (`apolloClient` is declared further down; this only runs on connect,
      // i.e. once something subscribes through that very client.)
      // Every (re)connect gets a token that will outlive the handshake — a
      // reconnect after laptop sleep would otherwise present the token that
      // expired while asleep and every re-subscribe would fail. A failed
      // refresh falls back to whatever is stored: the server then answers
      // with the precise auth error, and the HTTP layer's authErrorLink owns
      // the "session really is over" logout.
      if (isJwtExpiringWithin(getTokens().auth, WS_TOKEN_MIN_TTL_MS)) {
        await ensureFreshAccessToken(apolloClient).catch(() => undefined)
      }

      return {
        headers: getBaseHeaders(locale),
      }
    },
    keepAlive: WS_KEEPALIVE_MS,
    lazy: true,
    on: {
      closed: () => {
        clearSocketTimers()
      },
      connected: (socket) => {
        activeSocket = socket as WebSocket
        clearSocketTimers()

        // Close the socket just before its connect-time token expires so the
        // reconnect (via `connectionParams`) carries a fresh one. 4403 is a
        // non-fatal close code: graphql-ws retries and re-subscribes.
        const expiresAt = getJwtExpiresAt(getTokens().auth)
        if (expiresAt !== undefined) {
          tokenExpiryTimeout = setTimeout(
            () => {
              if (activeSocket?.readyState === WebSocket.OPEN) {
                activeSocket.close(CloseCode.Forbidden, 'Token expired')
              }
            },
            Math.max(0, expiresAt - Date.now() - WS_TOKEN_EXPIRY_CLOSE_MARGIN_MS),
          )
        }

        // A tab hidden for a long time does not need a live socket: drop it,
        // and `retryWait` below parks the reconnect until the tab is visible.
        // (Hidden tabs still get live delivery until then — that is what
        // powers the browser notifications while the app is in another tab.)
        const armStaleHidden = () => {
          clearTimeout(staleHiddenTimeout)
          staleHiddenTimeout = setTimeout(
            () => {
              if (getHiddenDuration() >= WS_STALE_HIDDEN_MS) {
                wsClient.terminate()
              } else {
                armStaleHidden()
              }
            },
            Math.max(1_000, WS_STALE_HIDDEN_MS - getHiddenDuration()),
          )
        }

        armStaleHidden()

        // Live-delivery reconcile signal: events missed while the socket was
        // down are not replayed, so messaging refetches on every RE-connect.
        reportWsConnected()
      },
      ping: (received) => {
        // `received: false` ⇒ we just SENT a ping; expect a pong promptly.
        if (!received) {
          pongTimeout = setTimeout(() => {
            if (activeSocket?.readyState === WebSocket.OPEN) {
              activeSocket.close(4408, 'Request Timeout')
            }
          }, WS_PONG_TIMEOUT_MS)
        }
      },
      pong: (received) => {
        // `received: true` ⇒ the server answered our ping; socket is alive.
        if (received && pongTimeout) {
          clearTimeout(pongTimeout)
          pongTimeout = undefined
        }
      },
    },
    // Default is 5; an operator session lives for hours, so a flaky network
    // must not permanently kill live delivery (reconcile-on-reconnect heals
    // whatever the dead socket missed).
    retryAttempts: Infinity,
    retryWait: async (retries) => {
      await sleep(
        Math.min(WS_RETRY_BASE_MS * 2 ** retries, WS_RETRY_MAX_MS) +
          Math.random() * WS_RETRY_JITTER_MS,
      )
      // Stale hidden tab: do not reconnect until someone is looking. Resolves
      // instantly for visible tabs and tabs hidden only briefly.
      if (getHiddenDuration() >= WS_STALE_HIDDEN_MS) {
        await whenPageVisible()
      }
    },
    url: import.meta.env.VITE_WS_URI || import.meta.env.VITE_API_URI,
  })

  const wsLink = new GraphQLWsLink(wsClient)

  const apolloClient = new ApolloClient({
    cache: new InMemoryCache({
      dataIdFromObject: (responseObject) => {
        switch (responseObject.__typename) {
          case 'PhoneNumber':
            if (responseObject.international) {
              return `PhoneNumber:${responseObject.international}`
            } else {
              return undefined
            }

          default:
            return defaultDataIdFromObject(responseObject)
        }
      },
      possibleTypes: fragmentTypes.possibleTypes,
      typePolicies: {
        Query: QueryPolicies,
      },
    }),
    clientAwareness: {
      name: pkg.name,
      version: pkg.version,
    },
    defaultOptions: {
      mutate: {
        errorPolicy: 'all',
      },
      watchQuery: {
        errorPolicy: 'all',
        fetchPolicy: 'cache-and-network',
        returnPartialData: false,
      },
    },
    link: ApolloLink.split(
      ({ query }) => {
        const def = getMainDefinition(query)
        return def.kind === 'OperationDefinition' && def.operation === 'subscription'
      },
      wsLink,
      httpLink,
    ),
  })

  // WS auth is frozen at connect time: recovering from an expired JWT on a
  // live socket means terminating it so the reconnect goes through
  // `connectionParams` again (see wsAuth.ts).
  registerWsTerminate(apolloClient, () => wsClient.terminate())

  return apolloClient
}
